import { addDays, addMinutes, startOfWeek } from "date-fns";
import { useCommunication } from "./CommunicationWrapper";

interface MicrosoftCalendarEvent {
    id?: string;
    subject: string;
    body?: {
        contentType: "HTML" | "Text";
        content: string;
    };
    start: {
        dateTime: string;
        timeZone: string;
    };
    end: {
        dateTime: string;
        timeZone: string;
    };
    categories?: string[];
    location?: {
        displayName: string;
    };
    singleValueExtendedProperties?: Array<{
        id: string;
        value: string;
    }>;
}

const BETTERCAMPUS_CALENDAR_NAME = "BetterCampus Calendar";
const BETTERCAMPUS_CALENDAR_DESCRIPTION = "Automatically synced events from BetterCampus";

type EventOperation = {
    event: MicrosoftCalendarEvent | null,
    op: "create" | "update" | "delete",
    calendar: string
}

export const useMicrosoftCalendarSync = () => {
    const { updateOptions, getValidCalendarToken, getOptions, origin, sendMessage } = useCommunication();

    const addToUnsyncedQueue = async (events: Record<string, EventOperation>) => {
        try {
            sendMessage({
                type: "microsoft_cal_add_to_queue",
                events: events
            });
        } catch (err) {
            console.error("[MicrosoftCalPush] Failed to send events to background queue", err);
        }
    };

    const handleInitialDueDates = async () => {
        const settings = getOptions("sync");
        const targetCalendar = settings?.microsoft_cal_push_settings?.betterCampusCalendarId || "";

        if (!settings?.microsoft_cal_push_settings?.pushDueDates || !settings?.microsoft_cal_push_settings?.pushEnabled) {
            return;
        }

        const params = new URLSearchParams();
        const ids = settings?.card_ids || [];
        ids.forEach((id: string) => params.append("context_codes[]", "course_" + id));

        const now = new Date().toISOString();
        const end = addDays(new Date(), 130).toISOString();

        let allEvents: Record<string, EventOperation> = {};
        let infiniteLoopBackoff = 1;
        let retrieved = 100;

        while (retrieved === 100 && infiniteLoopBackoff < 30) {
            try {
                const response = await fetch(`${origin}/api/v1/calendar_events?${params.toString()}&type=assignment&start_date=${now}&end_date=${end}&page=${infiniteLoopBackoff}&per_page=100`, {
                    method: "GET",
                });

                const data = await response.json();
                retrieved = data?.length || 0;
                infiniteLoopBackoff++;

                (data || []).forEach((calendarEvent: any) => {
                    const canvasId = `todo_${calendarEvent?.assignment?.id}`;

                    // Due dates are ALWAYS all-day events - set to midnight UTC
                    const dueDate = new Date(calendarEvent?.start_at);
                    const startDate = new Date(dueDate);
                    startDate.setUTCHours(0, 0, 0, 0);
                    const endDate = new Date(startDate);
                    endDate.setUTCDate(endDate.getUTCDate() + 1);

                    const event: MicrosoftCalendarEvent = {
                        subject: `[DUE] ${calendarEvent?.title}`,
                        body: {
                            contentType: "Text",
                            content: `Assignment due date\n\n[BetterCanvas ID: ${canvasId}]`,
                        },
                        start: {
                            dateTime: startDate.toISOString(),
                            timeZone: "UTC",
                        },
                        end: {
                            dateTime: endDate.toISOString(),
                            timeZone: "UTC",
                        },
                        isAllDay: true,
                        categories: ["BetterCanvas", "Assignment"],
                    } as any;

                    allEvents[canvasId] = { "event": event, "op": "create", "calendar": targetCalendar } as EventOperation;
                });
            } catch (err) {
                console.error("Error fetching calendar events", err);
            }
        }

        await addToUnsyncedQueue(allEvents);

        try {
            sendMessage({ type: "microsoft_cal_process_queue" });
        } catch (e) {
            console.warn("Failed to trigger background queue processing", e);
        }
    };

    const handleInitialPlanned = async () => {
        const settings = getOptions("sync");
        const targetCalendar = settings?.microsoft_cal_push_settings?.betterCampusCalendarId || "";

        if (!settings?.microsoft_cal_push_settings?.pushPlannedItems || !settings?.microsoft_cal_push_settings?.pushEnabled) {
            return;
        }

        const options = getOptions("sync");
        const planned = options?.planned || {};

        const events: Record<string, EventOperation> = {};

        Object.keys(planned).forEach((key) => {
            try {
                const details = planned[key];
                const plannedAt = details.plannedAt;
                if (!details.plannedAt || !details.duration) {
                    return;
                }

                const canvasId = `planned_${details.id}`;
                const courseData = options[`card_${details.course}`] || {};

                const date = new Date(plannedAt);
                const start = new Date(date);
                const end = addMinutes(start, details.duration);

                // Check if the planned date is in the past
                if (start.getTime() < Date.now()) {
                    return;
                }

                if (start.getTime() > end.getTime()) {
                    console.error("microsoft sync planned date is in the past somehow", details);
                    return;
                }

                const event: MicrosoftCalendarEvent = {
                    subject: `[WORK] ${details.title}`,
                    body: {
                        contentType: "Text",
                        content: `Course: ${courseData?.code || courseData?.default}\nPoints: ${details.points || "N/A"}\nLink: ${details.url}\n\n[BetterCanvas ID: ${canvasId}]`,
                    },
                    start: {
                        dateTime: start.toISOString(),
                        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                    },
                    end: {
                        dateTime: end.toISOString(),
                        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                    },
                    categories: ["BetterCanvas", "Planned"],
                };

                events[canvasId] = { "event": event, "op": "create", "calendar": targetCalendar } as EventOperation;
            } catch (err) {
                console.error("Error queueing planned item:", err);
            }
        });

        await addToUnsyncedQueue(events);

        try {
            sendMessage({ type: "microsoft_cal_process_queue" });
        } catch (e) {
            console.warn("Failed to trigger background queue processing", e);
        }
    };

    const handleInitialClassTimes = async () => {
        const options = getOptions("sync");
        const settings = options?.microsoft_cal_push_settings || {};
        const targetCalendar = settings?.betterCampusCalendarId || "";

        if (!targetCalendar || !settings?.pushClassTimes || !settings?.pushEnabled) return;

        const cardIds = options?.card_ids || [];
        const cards = cardIds.map((id: string) => `card_${id}`);

        const events: Record<string, EventOperation> = {};
        const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

        for (const courseId of cardIds) {
            const course = options[`card_${courseId}`];
            const typedCourse = course as any;
            const schedule = typedCourse?.schedule || [];

            if (schedule.length === 0) continue;

            for (const period of schedule) {
                try {
                    // Normalize start/end times
                    const start = Math.min(period.start, period.end);
                    const end = Math.max(period.start, period.end);

                    // Skip if start and end are the same
                    if (start === end) {
                        console.warn(`[MicrosoftSync] Skipping class time with equal start/end: ${courseId}, day ${period.day}`);
                        continue;
                    }

                    const canvasId = `class_${courseId}_${period.day}_${start}`;

                    const weekStart = startOfWeek(new Date(), { weekStartsOn: 0 });
                    const nextClass = new Date(weekStart);
                    nextClass.setDate(weekStart.getDate() + period.day);
                    nextClass.setHours(Math.floor(start / 60), start % 60, 0, 0);

                    const endTime = new Date(nextClass);
                    endTime.setHours(Math.floor(end / 60), end % 60, 0, 0);

                    const event: MicrosoftCalendarEvent = {
                        subject: typedCourse.code || typedCourse.default || `Course ${courseId}`,
                        body: {
                            contentType: "Text",
                            content: `Class session for ${typedCourse.default || typedCourse.code || courseId}\n\n[BetterCanvas ID: ${canvasId}]`,
                        },
                        start: {
                            dateTime: nextClass.toISOString(),
                            timeZone: timeZone,
                        },
                        end: {
                            dateTime: endTime.toISOString(),
                            timeZone: timeZone,
                        },
                        categories: ["BetterCanvas", "Class"],
                    };

                    // Add recurrence for weekly classes
                    (event as any).recurrence = {
                        pattern: {
                            type: "weekly",
                            interval: 1,
                            daysOfWeek: [["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"][period.day]],
                        },
                        range: {
                            type: "noEnd",
                        },
                    };

                    events[canvasId] = { "event": event, "op": "create", "calendar": targetCalendar } as EventOperation;
                } catch (err) {
                    console.error("Error queueing class time:", err);
                }
            }
        }

        await addToUnsyncedQueue(events);

        try {
            sendMessage({ type: "microsoft_cal_process_queue" });
        } catch (e) {
            console.warn("Failed to trigger background queue processing", e);
        }
    };

    const handleInitialMicrosoftSync = async () => {
        const settings = getOptions("sync")?.microsoft_cal_push_settings || {};

        if (settings?.pushDueDates) {
            await handleInitialDueDates();
        }

        if (settings?.pushPlannedItems) {
            await handleInitialPlanned();
        }

        if (settings?.pushClassTimes) {
            await handleInitialClassTimes();
        }
    };

    const deleteMicrosoftCalendar = async () => {
        const settings = getOptions("sync");
        const betterCampusCalendarId = settings?.microsoft_cal_push_settings?.betterCampusCalendarId || "";

        if (!betterCampusCalendarId) return;

        try {
            const validToken = await getValidCalendarToken();
            if (validToken) {
                const response = await fetch(`https://graph.microsoft.com/v1.0/me/calendars/${betterCampusCalendarId}`, {
                    method: 'DELETE',
                    headers: {
                        Authorization: `Bearer ${validToken}`,
                    },
                });

                if (response.ok) {
                    console.log('[MicrosoftCal] Successfully deleted BetterCampus calendar:', betterCampusCalendarId);
                } else {
                    console.warn('[MicrosoftCal] Failed to delete calendar, status:', response.status);
                }
            }
        } catch (error) {
            console.error('[MicrosoftCal] Error deleting calendar:', error);
        }
    };

    const listMicrosoftCalendars = async () => {
        const validToken = await getValidCalendarToken();
        if (!validToken) return;

        const response = await fetch('https://graph.microsoft.com/v1.0/me/calendars', {
            method: 'GET',
            headers: {
                Authorization: `Bearer ${validToken}`,
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            console.error('[MicrosoftCal] Failed to fetch calendars:', response.status, response.statusText);
            return;
        }

        const data = await response.json();
        const current = getOptions("sync").microsoft_calendars || {};
        const output: Record<string, any> = { ...current };

        // Track which calendars still exist
        const existingCalendarIds = new Set<string>();

        // Check if our BetterCampus calendar still exists
        const settings = getOptions("sync").microsoft_cal_push_settings || {};
        const betterCampusCalendarId = settings?.betterCampusCalendarId || "";
        let betterCampusCalendarExists = false;

        (data.value || []).forEach((calendar: any) => {
            existingCalendarIds.add(calendar.id);

            // Check if this is our BetterCampus calendar
            if (betterCampusCalendarId && calendar.id === betterCampusCalendarId) {
                betterCampusCalendarExists = true;
            }

            if (output[calendar.id]) {
                // Update existing calendar info
                output[calendar.id] = {
                    ...output[calendar.id],
                    name: calendar.name,
                    color: calendar.color,
                    lastSeen: Date.now(),
                };
            } else {
                // Add new calendar
                output[calendar.id] = {
                    id: calendar.id,
                    name: calendar.name,
                    color: calendar.color,
                    shouldSync: false,
                    lastSeen: Date.now(),
                };
            }
        });

        // Mark calendars as missing if they weren't in the latest fetch
        Object.keys(output).forEach(calendarId => {
            if (!existingCalendarIds.has(calendarId)) {
                output[calendarId] = {
                    ...output[calendarId],
                    missing: true,
                    lastSeen: output[calendarId].lastSeen || Date.now(),
                };
            }
        });

        updateOptions({
            microsoft_calendars: output,
            microsoft_cal_last_sync: Date.now(),
        }, "sync");

        // If BetterCampus calendar no longer exists, clear it from settings
        if (!betterCampusCalendarExists && betterCampusCalendarId) {
            updateOptions({
                microsoft_cal_push_settings: {
                    ...settings,
                    betterCampusCalendarId: null,
                    pushEnabled: false,
                }
            }, "sync");
        }
    };

    const resetMicrosoftCalendar = async () => {
        const settings = getOptions("sync").microsoft_cal_push_settings || {};
        await deleteMicrosoftCalendar();
        await updateOptions({
            "microsoft_cal_push_settings": {
                ...settings,
                betterCampusCalendarId: null,
            },
        }, "sync");
        await updateOptions({ microsoft_cal_pushed_events: {} }, "local");
    };

    const disconnectMicrosoftCalendar = async () => {
        await deleteMicrosoftCalendar();
        const settings = getOptions("sync");
        await updateOptions({
            "microsoft_cal_sync_token": null,
            "microsoft_cal_push_settings": {
                ...settings?.microsoft_cal_push_settings,
                betterCampusCalendarId: null,
                pushEnabled: false,
            },
            "microsoft_cal_last_sync": null,
            "microsoft_calendars": {}
        }, "sync");
        await updateOptions({ microsoft_cal_pushed_events: {} }, "local");
    };

    const createBetterCampusMicrosoftCalendar = async (): Promise<string | null> => {
        const validToken = await getValidCalendarToken();
        if (!validToken) return null;

        try {
            const response = await fetch('https://graph.microsoft.com/v1.0/me/calendars', {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${validToken}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    name: BETTERCAMPUS_CALENDAR_NAME,
                    color: "auto",
                }),
            });

            if (!response.ok) {
                const errorText = await response.text();
                console.error(`[MicrosoftCal] Failed to create calendar. Status: ${response.status}, Error:`, errorText);
                return null;
            }

            const calendar = await response.json();
            console.log('[MicrosoftCal] Created BetterCampus calendar:', calendar.id);
            return calendar.id;
        } catch (error) {
            console.error('[MicrosoftCal] Error creating calendar:', error);
            return null;
        }
    };

    const ensureBetterCampusMicrosoftCalendar = async (): Promise<string | null> => {
        const options = getOptions("sync");
        const settings = options?.microsoft_cal_push_settings || {};

        if (settings?.betterCampusCalendarId) {
            return settings.betterCampusCalendarId;
        }

        // Calendar doesn't exist or is invalid, create a new one
        const newCalendarId = await createBetterCampusMicrosoftCalendar();

        if (newCalendarId) {
            // Update settings with new calendar ID
            updateOptions({
                microsoft_cal_push_settings: {
                    ...settings,
                    betterCampusCalendarId: newCalendarId,
                }
            }, "sync");
        }

        return newCalendarId;
    };

    return {
        handleInitialMicrosoftSync,
        deleteMicrosoftCalendar,
        disconnectMicrosoftCalendar,
        resetMicrosoftCalendar,
        listMicrosoftCalendars,
        ensureBetterCampusMicrosoftCalendar,
    };
};
